import React, { useCallback, useEffect, useRef, useState } from "react";
import { MessageOutlined } from "@ant-design/icons";
import { Badge, Splitter } from "antd";
import { useAccess } from "@umijs/max";
import { useLauncherDock } from "@/hooks/useLauncherDock";
import { useChat } from "./ChatContext";
import ChatLauncher from "./ChatLauncher";
import ChatPanel from "./ChatPanel";
import "./Chat.less";

const WIDGET_SIZE_KEY = "FR_CHAT_WIDGET_SIZE";
const SIDEBAR_WIDTH_KEY = "FR_CHAT_SIDEBAR_WIDTH";

const WIDGET_MIN_W = 320;
const WIDGET_MAX_W = 720;
const WIDGET_MIN_H = 380;
const WIDGET_MAX_H = 860;

const SIDEBAR_MIN_W = 320;
const SIDEBAR_MAX_W = 900;

function readWidgetSize(): { width: number; height: number } {
  try {
    const raw = localStorage.getItem(WIDGET_SIZE_KEY);
    if (raw) {
      const v = JSON.parse(raw);
      if (typeof v?.width === "number" && typeof v?.height === "number") return v;
    }
  } catch {
    // ignore
  }
  return { width: 400, height: 560 };
}

function readSidebarWidth(): number {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isNaN(n)) return n;
  } catch {
    // ignore
  }
  return 480;
}

/**
 * Global mode-switching shell — mounted once in app.tsx's childrenRender,
 * wrapping the routed page content, alongside CallWidget. Renders `children`
 * untouched until the chat feature has resolved as available (ready ===
 * false, e.g. not configured / unreachable) so it never intrudes on the
 * layout for a tenant where Zulip isn't set up, and likewise for a user
 * who lacks the chat.view permission.
 *
 * Sidebar mode actually reflows the page via antd's `Splitter` (the same
 * resizable-pane component already used by GpsJobBoard/DriverDeviceRoute),
 * so the chat pane's width is user-adjustable and remembered, rather than a
 * fixed 440px that turned out cramped in practice. Widget mode leaves
 * `children` completely untouched and only adds a floating, resizable
 * sibling popup.
 */
const ChatWidget: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { ready, mode, open, toggleOpen, unreadTotal, setCanManageChatChannels } = useChat();
  const access = useAccess();

  // ChatProvider is mounted above the access plugin's own provider (see the
  // comment on rootContainer in app.tsx), so it can't call useAccess() itself
  // — this is mounted underneath it, so it pushes the permission down instead.
  useEffect(() => {
    setCanManageChatChannels(access.canManageChatChannels);
  }, [access.canManageChatChannels, setCanManageChatChannels]);

  // ---- widget-mode drag-to-reposition (mirrors CallWidget's own topbar drag) ----
  const [popupPos, setPopupPos] = useState<{ x: number; y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const popupRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; startPosX: number; startPosY: number } | null>(null);

  const handleHeaderMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest("button")) return;
    e.preventDefault();
    const el = popupRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragRef.current = { startX: e.clientX, startY: e.clientY, startPosX: rect.left, startPosY: rect.top };
    setIsDragging(true);
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return;
      setPopupPos({
        x: Math.max(0, Math.min(window.innerWidth - 80, dragRef.current.startPosX + ev.clientX - dragRef.current.startX)),
        y: Math.max(0, Math.min(window.innerHeight - 60, dragRef.current.startPosY + ev.clientY - dragRef.current.startY)),
      });
    };
    const onUp = () => {
      dragRef.current = null;
      setIsDragging(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  // Inner slot of the shared Call/Chat launcher dock (CallWidget is slot 0) —
  // the FAB drags the whole dock, which snaps to the left/right edge.
  const dock = useLauncherDock(1);

  // ---- widget-mode resize — the handle sits on the popup corner facing away
  // from the dock's anchor (top-left when docked bottom-right, the default),
  // so the popup grows away from the FAB. ----
  const resizeFromRight = dock.side === "left";
  const resizeFromBottom = dock.anchorTop;
  const [popupSize, setPopupSize] = useState(readWidgetSize);
  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  const handleResizeMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, startW: popupSize.width, startH: popupSize.height };
    setIsResizing(true);
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const { startX, startY, startW, startH } = resizeRef.current;
      const dx = (ev.clientX - startX) * (resizeFromRight ? 1 : -1);
      const dy = (ev.clientY - startY) * (resizeFromBottom ? 1 : -1);
      const width = Math.max(WIDGET_MIN_W, Math.min(WIDGET_MAX_W, startW + dx));
      const height = Math.max(WIDGET_MIN_H, Math.min(WIDGET_MAX_H, startH + dy));
      setPopupSize({ width, height });
    };
    const onUp = () => {
      resizeRef.current = null;
      setIsResizing(false);
      setPopupSize((size) => {
        try {
          localStorage.setItem(WIDGET_SIZE_KEY, JSON.stringify(size));
        } catch {
          // ignore
        }
        return size;
      });
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [popupSize, resizeFromRight, resizeFromBottom]);

  // ---- sidebar-mode resize (antd Splitter — same component GpsJobBoard/
  // DriverDeviceRoute already use for resizable panes) ----
  const [sidebarWidth] = useState(readSidebarWidth);
  const handleSidebarResizeEnd = useCallback((sizes: number[]) => {
    const width = sizes[1];
    if (typeof width === "number") {
      try {
        localStorage.setItem(SIDEBAR_WIDTH_KEY, String(Math.round(width)));
      } catch {
        // ignore
      }
    }
  }, []);

  if (!ready || !access.canViewChat) return <>{children}</>;

  if (mode === "sidebar") {
    if (!open) {
      // Floats over the page instead of reflowing it (same pattern as widget
      // mode's launcher below) — wrapping children in the flex row here would
      // shrink maxWidth-capped page content by the rail's width just to show
      // a collapsed tab.
      return (
        <>
          {children}
          <div className="chat-sidebar-rail" onClick={toggleOpen}>
            <Badge count={unreadTotal} overflowCount={99} size="small">
              <MessageOutlined />
            </Badge>
          </div>
        </>
      );
    }
    return (
      <Splitter layout="horizontal" className="chat-layout-with-sidebar" onResizeEnd={handleSidebarResizeEnd}>
        <Splitter.Panel min={200}>
          <div className="chat-layout-with-sidebar__content">{children}</div>
        </Splitter.Panel>
        <Splitter.Panel defaultSize={sidebarWidth} min={SIDEBAR_MIN_W} max={SIDEBAR_MAX_W} style={{ overflow: "visible", minWidth: 0 }}>
          <div className="chat-sidebar-dock">
            <ChatPanel variant="sidebar" />
          </div>
        </Splitter.Panel>
      </Splitter>
    );
  }

  return (
    <>
      {children}
      <div className="chat-widget-container" style={dock.containerStyle}>
        {open && (
          <div
            ref={popupRef}
            className={`chat-widget-popup${isDragging || isResizing ? " chat-widget-popup--dragging" : ""}`}
            style={{
              width: popupSize.width,
              height: popupSize.height,
              ...(popupPos ? { position: "fixed", left: popupPos.x, top: popupPos.y } : undefined),
            }}
          >
            <div
              className={`chat-widget-popup__resize-handle chat-widget-popup__resize-handle--${resizeFromBottom ? "b" : "t"}${resizeFromRight ? "r" : "l"}`}
              onMouseDown={handleResizeMouseDown}
            />
            <ChatPanel variant="widget" onHeaderMouseDown={handleHeaderMouseDown} />
          </div>
        )}
        <ChatLauncher fabProps={dock.fabProps} dragging={dock.isDraggingThis} />
      </div>
    </>
  );
};

export default ChatWidget;
